<template>
  <b-card class="shadow atm-rounded px-4">
    <b-overlay
      id="overlay-background"
      :show="updatingGraphs"
      variant="transparent"
      opacity="1.00"
      blur="2px"
      rounded="sm"
    >
      <b-card-title class="px-2 py-2">{{ chartTitle }}</b-card-title>
      <section>
        <section class="d-flex flex-wrap justify-content-between align-items-center mx-2 gap-4">
          <b-card-title class="py-2">{{ dateRange }}</b-card-title>
          <section class="d-flex gap-4 align-items-center justify-content-center">
            <select
              v-model="weeklyOrMonthly"
              @change="togglePeriod"
              class="weekly-or-monthly-select h-100 atm-input"
            >
              <option v-for="(value, key) in selectOptions" :key="key" :value="value">
                {{ value }}
              </option>
            </select>
            <b-button @click="lastWeekOrMonth" variant="light" class="border border-dark">
              <i class="material-icons align-middle">arrow_back_ios</i>
            </b-button>
            <b-button
              @click="nextWeekOrMonth"
              variant="light"
              class="border border-dark"
              :disabled="buttonNextWeekDisabled"
            >
              <i class="material-icons align-middle">arrow_forward_ios</i>
            </b-button>
          </section>
        </section>
        <section v-if="data && chartSeries.length">
          <apexchart ref="apexchart" :options="chartOptions" :series="chartSeries"></apexchart>
        </section>
        <section v-else>
          <no-dashboard-graphics-view></no-dashboard-graphics-view>
        </section>
      </section>
      <template #overlay>
        <div class="text-center">
          <b-spinner class="mb-2" label="Spinning"></b-spinner>
          <p id="cancel-label">Please wait...</p>
        </div>
      </template>
    </b-overlay>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import NoDashboardGraphicsView from '@/dashboard/views/NoDashboardGraphicsView';
import { dateFormatAbbreviatedNameOfDayAndNumberedDay } from '@/common/utils/Date';
import { SELECT_OPTIONS, linearChartResponsive } from '@/dashboard/constants';

export default {
  components: {
    apexchart: VueApexCharts,
    NoDashboardGraphicsView,
  },
  props: {
    data: Array,
    dateRange: { type: String, default: '' },
    chartType: { type: String, default: 'area' },
    chartTitle: { type: String, default: '' },
  },
  data() {
    return {
      weekOrMonth: 0,
      weeklyOrMonthly: SELECT_OPTIONS.Weekly,
      selectOptions: SELECT_OPTIONS,
      updatingGraphs: false,
      chartOptions: {
        chart: {
          height: 350,
          type: this.chartType,
          zoom: {
            enabled: false,
          },
        },
        legend: {
          show: true,
          markers: {
            width: 0,
            height: 0,
          },
          formatter: this.formatLegendText,
          onItemHover: {
            highlightDataSeries: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: [5, 7, 5],
          curve: 'straight',
        },
        markers: {
          size: 0,
          hover: {
            sizeOffset: 6,
          },
        },
        xaxis: {
          type: 'category',
          categories: [],
        },
        grid: {
          borderColor: '#f1f1f1',
        },
        responsive: linearChartResponsive,
      },
      chartSeries: [],
    };
  },
  computed: {
    processChartData() {
      const categories = [];
      const series = [];

      this.data.forEach((item) => {
        item.modules.forEach((module) => {
          const formatedDate = dateFormatAbbreviatedNameOfDayAndNumberedDay(item.date);
          categories.push(formatedDate);

          if (!series.find((serie) => serie.name === module.name)) {
            series.push({
              name: module.name,
              data: [],
            });
          }

          const moduleIndex = series.findIndex((serie) => serie.name === module.name);
          series[moduleIndex].data.push(module.totals);
        });
      });

      const uniqueCategories = [...new Set(categories)];

      return {
        xaxis: {
          ...this.chartOptions.xaxis,
          categories: uniqueCategories,
        },
        series,
      };
    },
    buttonNextWeekDisabled() {
      return this.weekOrMonth === 0;
    },
  },
  methods: {
    assigningChartData() {
      const { xaxis, series } = this.processChartData;

      this.chartOptions.xaxis = xaxis;
      this.chartSeries = series;

      this.$refs.apexchart?.updateOptions({
        xaxis,
      });
    },
    formatLegendText(nameOfSeries, opts) {
      const iconName =
        opts.w.globals.series[opts.seriesIndex].length > 0 ? 'visibility' : 'visibility_off';
      const iconColor = opts.w.globals.colors[opts.seriesIndex];
      return `<div class="d-flex gap-2 align-items-center justify-content-center text-center">
        <span class="material-icons h5" style="color: ${iconColor};">${iconName}</span>
        <span class="mb-2 font-weight-bold">${nameOfSeries}</span>
        </div>`;
    },
    nextWeekOrMonth() {
      if (this.weekOrMonth > 0) {
        this.weekOrMonth--;
        this.selectedWeekOrMonth();
      }
    },
    lastWeekOrMonth() {
      this.weekOrMonth++;
      this.selectedWeekOrMonth();
    },
    selectedWeekOrMonth() {
      const goBackWeekOrMonth = this.weekOrMonth;
      this.updatingGraphs = true;
      this.$emit('weeklyOrMonthly', { goBackWeekOrMonth, weeklyOrMonthly: this.weeklyOrMonthly });
    },
    togglePeriod() {
      this.weekOrMonth = 0;
      this.selectedWeekOrMonth();
    },
  },
  mounted() {
    if (!this.data) return;
    this.assigningChartData();
  },
  watch: {
    data: function() {
      this.assigningChartData();
      this.updatingGraphs = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.weekly-or-monthly-select {
  min-width: 50%;
}
</style>
